import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { RHFDateTimePicker } from "../../../../../../../RHFComponents/RHFComponents";
import dayjs, { Dayjs } from "dayjs";
import { pgmedtage, pgmedv } from "../../../../../../../../networking";
import { AllertMessage } from "../MedintAgendaFormDetail";

type Props = {
  open: boolean;
  onClose: () => void;
  action: "insert" | "edit" | "copy";
  initialData?: any;
  parentMethods: UseFormReturn<any>;
  createdIdPgmedt?: string;
};

type PeriodiForm = {
  periodiValidita: {
    dataInizio: any;
    dataFine: any;
  };
};

const buildDefault = (): PeriodiForm => ({
  periodiValidita: {
    dataInizio: "",
    dataFine: "",
  },
});

const PeriodiValiditaModal = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<PeriodiForm | null>(null);

  const modalMethods = useForm<PeriodiForm>({
    defaultValues: buildDefault(),
    mode: "onChange",
  });

  useEffect(() => {
    if (!props.open) return;
    setError(null);

    if (props.action === "insert") {
      modalMethods.reset(buildDefault());
      return;
    }

    console.log(props.initialData);

    modalMethods.reset({
      periodiValidita: {
        dataInizio: props.initialData?.DATAINIZIOVALIDITA,
        dataFine: props.initialData?.DATAFINEVALIDITA,
      },
    });
  }, [props.open, props.action, props.initialData, modalMethods]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = modalMethods.handleSubmit((values) => {
    setPendingSubmit(values);

    setSeverity("warning");
    setMessage(
      props.action === "insert"
        ? "Confermi l'inserimento del periodo?"
        : props.action === "edit"
          ? "Confermi la modifica del periodo?"
          : "Confermi la copia del periodo?",
    );
    setShowAllert(true);
  });

  const refreshProgram = async () => {
    const idPgmedt =
      props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
      (props.createdIdPgmedt as string | undefined);
    if (!idPgmedt)
      throw new Error("Manca la chiave agenda (medIntNuovoProgramma.chiave).");

    const response = await pgmedtage({ crud: "R", codicePgmedt: "", idPgmedt });
    const data = response?.data?.dati?.[0];
    if (!data) return;

    // aggiorno solo le tipologie: dentro ci sono anche le giornate e gli orari,
    // quindi si aggiorna tutto a cascata
    props.parentMethods.setValue(
      "medIntNuovoProgramma.periodiVal",
      data.periodiValidita ?? [],
      {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  };

  const submitPeriodo = async () => {
    if (!pendingSubmit) return;
    setError(null);
    setLoading(true);

    try {
      const idPgmedt =
        props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
        (props.createdIdPgmedt as string | undefined);

      if (!idPgmedt) throw new Error("Manca chiave agenda.");

      if (props.action === "insert" && !idPgmedt) {
        throw new Error(
          "Seleziona prima una tipologia (serve per inserire una giornata).",
        );
      }

      if (
        props.action !== "insert" &&
        !props.initialData?.IDPGMEDV &&
        !props.initialData?.IDPGMEDV_PRGINT
      ) {
        throw new Error(
          "Manca ID giornata (IDPGMEDG/PGMEDG_PRGINT) per la modifica.",
        );
      }

      const formValues = pendingSubmit.periodiValidita;
      const crud = props.action === "insert" ? "c" : "u";

      const response = await pgmedv({
        crud,
        prgint: props.initialData?.IDPGMEDV,
        dati: {
          pgmedv: {
            PGMEDT_PRGINT: idPgmedt,
            PGMEDV_DATA_INI: formValues.dataInizio
              ? dayjs(formValues.dataInizio).format("YYYY-MM-DD")
              : "",
            PGMEDV_DATA_FINE: formValues.dataFine
              ? dayjs(formValues.dataFine).format("YYYY-MM-DD")
              : "",
          },
        },
      });

      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }

      await refreshProgram();
      setTimeout(() => props.onClose(), 800);
    } catch (e: any) {
      console.error("Errore salvataggio prestazione:", e);
      setSeverity("error");
      setMessage(e?.message ?? "Errore durante il salvataggio.");
    } finally {
      setPendingSubmit(null);
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? submitPeriodo : null}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal open={props.open}   onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          props.onClose();
        }}>
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "70dvh",
            width: "100%",
            overflow: "auto",
            paddingTop: 1,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormProvider {...modalMethods}>
            <Tabs
              value={0}
              sx={{
                paddingLeft: 2,
                "& .MuiTabs-indicator": { backgroundColor: "#003267" },
              }}
            >
              <Tab
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  textTransform: "none",
                  display: "flex",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  color: "#003267 !important",
                }}
                label="Gestione periodo validità del programma del medico"
              />
            </Tabs>
            <Grid
              container
              spacing={2}
              padding={2}
              display={"flex"}
              sx={{ height: "100%", mt: 0.5 }}
            >
              <Grid item xs={12} md={12}>
                <RHFDateTimePicker
                  control={modalMethods.control}
                  name="periodiValidita.dataInizio"
                  label="Data Inizio Validità"
                  size="small"
                  displayFormat="DD/MM/YY"
                  valueFormat="YYYY-MM-DD"
                  fullWidth
                  dateOnly
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RHFDateTimePicker
                  control={modalMethods.control}
                  name="periodiValidita.dataFine"
                  label="Data Fine Validità"
                  size="small"
                  displayFormat="DD/MM/YY"
                  valueFormat="YYYY-MM-DD"
                  fullWidth
                  dateOnly
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
              >
                <Button variant={"outlinedCustom"} onClick={props.onClose}>
                  Uscita
                </Button>
                <Button
                  variant={"containedCustom"}
                  onClick={askConfirmSubmit}
                >
                  Conferma
                </Button>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default PeriodiValiditaModal;
